import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import { pluralize } from "@entrilia/shared/utilities/stringHelper";
import type {
  ContactLastEmailDelivery,
  ContactPasswordReset,
  ContactPermissions,
  ContactRegistrationStory,
  ContactSignInState,
  EmailRegistrationEligibilityStatus,
  EmailSuppressionState,
  UserMfaStatus,
} from "../../../../../../api/types/fundStructureTypes";
import { portalRoleCaptionMap } from "../../../../../../utilities/enumCaptions";
import { intercomArticleIds } from "../../../../../../utilities/intercomArticles";
import { orderedPortalRoles } from "../../../common/matrixHelper";

export type PortalAccessRowStatus = "problem" | "completed";

export interface PortalAccessRowModel {
  value: string;
  caption?: string;
  help?: string;
  helpDetails?: string;
  status?: PortalAccessRowStatus;
  captionStatus?: Extract<PortalAccessRowStatus, "problem">;
}

const ownAccountExplanation =
  "This contact already has their own Entrilia account from previous use of the platform. There is no need to " +
  "register again — once the contact has access to your portal they sign in with their existing password.";

const anotherOrganizationResetInstruction =
  "This contact also signs in to a portal run by another administrator, so you can’t change their password here. " +
  "Ask the contact to use “Forgot password” on the Investor Portal sign-in page.";

const otherClientResetInstruction =
  "This contact’s password also opens a portal you don’t manage. Someone who manages every portal it opens can " +
  "reset it, or Entrilia Support can help.";

export const accessRolesExplanation =
  "This contact has a role per investor — Admin (can also manage other contacts’ access for that investor) or Member " +
  "(view access only). The full list of investors and their roles is under “Manage access”.";

export const manageAccessTooltip =
  "Opens the Communication Matrix, where you set this contact’s role and document categories for each investor.";

export const viewEmailsTooltip =
  "Opens the Emails tab, where you can check what happened to emails sent to this contact.";

export const resumeDeliveryTooltip =
  "Removes this address from our email provider's blocked list, so we start trying to deliver to it again.";

const impersonationBlockedTooltip =
  "You can view the portal as this contact once they have an email address and access to it.";

const impersonationTooltip =
  "Opens your portal exactly as this contact sees it. It is view-only — nothing you do here changes what they see.";

export const supportChatUnavailableTooltip = "The Entrilia Support chat isn’t available right now.";

export interface DocumentActivityGate {
  canView: boolean;
  tooltip: string;
}

export const getDocumentActivityGate = (options: {
  hasDocumentActivity?: boolean;
  isLoading: boolean;
}): DocumentActivityGate => {
  if (options.isLoading) {
    return { canView: false, tooltip: "Checking whether this contact has opened any documents…" };
  }
  if (options.hasDocumentActivity === undefined) {
    return {
      canView: false,
      tooltip: "We couldn’t check whether this contact has opened any documents. Reload the page to try again.",
    };
  }
  if (!options.hasDocumentActivity) {
    return { canView: false, tooltip: "This contact hasn’t opened any documents in your portal yet." };
  }
  return { canView: true, tooltip: "Opens Document Activity, showing only the documents this contact opened." };
};

const emailRegistrationResetInstruction =
  "This contact is part-way through registering by email. Resetting their password now would cancel it.";

export const getAccessSummary = (
  permissions: ContactPermissions[]
): PortalAccessRowModel & { investorNames: string[] } => {
  const granted = permissions.filter((p) => p.roles.some((role) => orderedPortalRoles.includes(role)));
  if (granted.length === 0) {
    return {
      value: "No access",
      caption: "Grant access to funds and investors to get started.",
      investorNames: [],
    };
  }

  const roleCaptions = distinct(granted.flatMap((p) => p.roles).map((role) => portalRoleCaptionMap[role]));
  const investorNames = distinct(granted.map((p) => p.investorName));
  const investorsText = `${investorNames.length} ${pluralize("investor", investorNames.length)}`;

  return {
    value: `${roleCaptions.sort().join(" & ")} · ${investorsText}`,
    investorNames,
  };
};

export const getContactedRow = (options: {
  invitationSentAtText?: string;
  lastEmailDelivery?: ContactLastEmailDelivery;
  bouncedAtText?: string;
  hasEmail: boolean;
  isDeliveryStatusLoaded: boolean;
  suppressionState?: EmailSuppressionState;
}): PortalAccessRowModel & { hasDeliveryDetail: boolean } => {
  const value = options.invitationSentAtText ? `Invitation sent ${options.invitationSentAtText}` : "No invitation sent";
  const delivery = options.lastEmailDelivery;

  if (delivery?.failed) {
    const bouncedOn = options.bouncedAtText ? ` on ${options.bouncedAtText}` : "";
    const ourMail = delivery.kind === "Invitation" ? "invitation" : "email";
    return {
      value,
      captionStatus: "problem",
      hasDeliveryDetail: true,
      caption:
        delivery.sender === "Portal"
          ? `Our ${ourMail} to this address bounced${bouncedOn}.`
          : `A recent email to this address bounced${bouncedOn}. An invitation may not reach them either.`,
      help: delivery.failureMessage || delivery.reason,
      helpDetails: delivery.failureMessage ? delivery.reason : undefined,
    };
  }
  if (options.isDeliveryStatusLoaded && delivery === undefined && options.hasEmail) {
    return {
      value,
      hasDeliveryDetail: true,
      caption: "We couldn’t check whether recent emails to this contact were delivered.",
    };
  }
  return { value, hasDeliveryDetail: options.suppressionState === "BounceSuppressed" };
};

export interface InvitationGate {
  canSend: boolean;
  tooltip: string;
}

export const getInvitationGate = (options: {
  hasEmail: boolean;
  hasPortalAccess: boolean;
  suppressionState?: EmailSuppressionState;
  isLoading?: boolean;
}): InvitationGate => {
  if (!options.hasPortalAccess) {
    return {
      canSend: false,
      tooltip: "Give this contact access to your portal first — an invitation links to a portal they cannot open yet.",
    };
  }
  if (!options.hasEmail) {
    return { canSend: false, tooltip: "Add an email address for this contact first — an invitation is sent by email." };
  }
  if (options.isLoading) {
    return { canSend: false, tooltip: "Checking whether emails to this contact are being delivered…" };
  }
  if (options.suppressionState === "BounceSuppressed") {
    return {
      canSend: false,
      tooltip:
        "The mail system has stopped delivering to this address after earlier bounces, so an invitation will not " +
        "arrive. Check the Emails tab, then confirm the address with the contact.",
    };
  }
  return { canSend: true, tooltip: "Emails this contact a link to your portal." };
};

export interface ImpersonationGate {
  canImpersonate: boolean;
  tooltip: string;
}

export const getImpersonationGate = (options: { hasEmail: boolean; hasPortalAccess: boolean }): ImpersonationGate =>
  options.hasEmail && options.hasPortalAccess
    ? { canImpersonate: true, tooltip: impersonationTooltip }
    : { canImpersonate: false, tooltip: impersonationBlockedTooltip };

export interface EmailDeliveryRowModel extends PortalAccessRowModel {
  help: string;
  isBlocked: boolean;
}

export const getEmailDeliveryRow = (suppressionState?: EmailSuppressionState): EmailDeliveryRowModel | undefined => {
  if (suppressionState === "BounceSuppressed") {
    return {
      value: "Emails to this address are being blocked",
      status: "problem",
      isBlocked: true,
      help:
        "Our email provider stopped sending to this address after an earlier email bounced. Until that is lifted " +
        "the contact receives nothing from your portal — invitations, document notifications, everything. Once " +
        "the mailbox problem is fixed, use “Resume delivery” to start sending again.",
    };
  }
  if (suppressionState === "Unknown") {
    return {
      value: "Couldn’t be checked",
      isBlocked: false,
      help: "We couldn’t check whether our email provider is blocking this address. Reload the page to try again.",
    };
  }
  return undefined;
};

export const getRegistrationRow = (options: {
  story?: ContactRegistrationStory;
  isRegistered: boolean;
  hasPortalAccess: boolean;
  registrationDateText?: string;
  isStoryLoading?: boolean;
}): PortalAccessRowModel => {
  const { story, isRegistered, hasPortalAccess, registrationDateText, isStoryLoading } = options;

  switch (story) {
    case "NotRegistered":
      return {
        value: "Not registered",
        caption: hasPortalAccess
          ? "The contact has access, and can register from the invitation link or the sign-in page."
          : "The contact can register once they have portal access.",
      };
    case "RegisteredAndActive":
      return {
        value: "Completed — password set",
        caption: registrationDateText ? `Registered ${registrationDateText}` : undefined,
        status: "completed",
      };
    case "AlreadyRegisteredOnEntrilia":
      return { value: "Already registered on Entrilia", help: ownAccountExplanation };
    case "RegisteredAwaitingFirstSignIn":
      return { value: "Completed — hasn’t signed in to your portal yet", status: "completed" };
    case "RegistrationByEmailInProgress":
      return { value: "Not finished — registering by email" };
    default:
      if (!isRegistered) {
        return { value: "Not registered" };
      }
      return isStoryLoading
        ? { value: "Registered" }
        : {
            value: "Registered on Entrilia",
            help:
              "This contact has an Entrilia account. We couldn’t check whether they completed registration for your " +
              "portal.",
          };
  }
};

export const getSignInEmailRow = (portalLogin?: string): PortalAccessRowModel => ({
  value: portalLogin || "No sign-in email yet",
  caption: "The address this contact signs in with.",
});

export const registrationByEmailExplanation =
  "A fallback for contacts whose mail system blocks the sign-up code: the contact starts it from the sign-up page, " +
  "confirms by email, and Entrilia sets up their account from that reply. Only the contact can start it.";

const blockedAddressCaption =
  "Registering by email needs a reply to this address, so it can’t finish while delivery is blocked. Resume " +
  "delivery first.";

export const getRegistrationByEmailRow = (
  status?: EmailRegistrationEligibilityStatus,
  suppressionState?: EmailSuppressionState
): PortalAccessRowModel | undefined => {
  const isAddressBlocked = suppressionState === "BounceSuppressed";

  switch (status) {
    case "Eligible":
      return {
        value: "Not started",
        caption: isAddressBlocked
          ? blockedAddressCaption
          : "If the sign-up code never reaches the contact, they can register by email from the sign-up page instead.",
      };
    case "InProgressAwaitingReply":
      return { value: "Waiting for the contact to reply", caption: "The request expires after 7 days." };
    case "InProgressSettingUp":
      return { value: "Setting up the contact’s account" };
    case "InProgressAwaitingFirstSignIn":
      return {
        value: "Temporary password sent",
        caption: "Valid for 72 hours. Registration finishes when the contact signs in.",
      };
    case "Expired":
      return {
        value: "Expired — no reply within 7 days",
        caption: isAddressBlocked ? blockedAddressCaption : "The contact can start it again from the sign-up page.",
      };
    case "RejectedAccessRemoved":
      return {
        value: "Stopped — the contact lost portal access before it finished",
        caption: "Restore access, and the contact can start again from the sign-up page.",
      };
    case "Rejected":
      return { value: "Stopped for a security reason", caption: "Entrilia Support can help." };
    default:
      return undefined;
  }
};

export const activityExplanation =
  "Counts anything this contact does in your portal — signing in, opening a document, submitting a change request " +
  "or metrics. What you do while impersonating them is never counted.";

export const getActivityRow = (options: {
  lastActivityText?: string;
  hasUndatedActivity?: boolean;
}): PortalAccessRowModel => {
  const valueWithoutDate = options.hasUndatedActivity ? "Yes — date not recorded" : "No activity recorded";

  return { value: options.lastActivityText ?? valueWithoutDate, help: activityExplanation };
};

export type MfaState = "On" | "Off" | "Unknown";

export const mfaStateLabels: Record<MfaState, string> = {
  On: "On",
  Off: "Off",
  Unknown: "Couldn’t be checked",
};

export interface MfaRowModel {
  state: MfaState;
  caption?: string;
  help: string;
}

export const getMfaRow = (options: {
  status?: UserMfaStatus;
  isStatusLoaded: boolean;
  isRegistered: boolean;
}): MfaRowModel | undefined => {
  switch (options.status) {
    case "MandatoryMFA":
      return {
        state: "On",
        caption: "every sign-in",
        help: "The contact is asked for an emailed security code at every sign-in.",
      };
    case "ConditionalMFA":
      return {
        state: "On",
        caption: "only when risky",
        help: "The contact is asked for an emailed security code only when a sign-in looks unusual.",
      };
    case "NoMFA":
      return {
        state: "Off",
        help:
          "The contact is never asked for an emailed security code. Registering by email always turns this off, " +
          "and only Entrilia Support can turn it back on.",
      };
    default:
      return options.isStatusLoaded && options.isRegistered
        ? {
            state: "Unknown",
            help: "We couldn’t check whether this contact is asked for an emailed security code.",
          }
        : undefined;
  }
};

const getPasswordResetTooltip = (passwordReset?: ContactPasswordReset): string | undefined => {
  if (!passwordReset) {
    return "We couldn’t check whether a password reset is available for this contact. Please try again later.";
  }
  if (passwordReset.canReset) {
    return (
      "Generates a temporary password for you to pass to this contact. It is shown once, and they must change " +
      "it when they sign in."
    );
  }
  switch (passwordReset.disabledReason) {
    case "NotRegistered":
      return "There’s no password to reset yet — this contact hasn’t registered.";
    case "RegistrationByEmailInProgress":
      return emailRegistrationResetInstruction;
    case "PortalAccessInAnotherOrganization":
      return anotherOrganizationResetInstruction;
    case "MissingResetPermissionInOtherClients":
      return otherClientResetInstruction;
    default:
      return "A password reset is not available for this contact.";
  }
};

export interface PasswordResetGate {
  canReset: boolean;
  tooltip?: string;
}

export const getPasswordResetGate = (options: {
  passwordReset?: ContactPasswordReset;
  isGateUnknown: boolean;
  isAccountDisabled: boolean;
}): PasswordResetGate => {
  if (options.isGateUnknown) {
    return { canReset: false, tooltip: "Checking whether a password reset is available…" };
  }
  if (options.isAccountDisabled) {
    return {
      canReset: false,
      tooltip:
        "Sign-in is disabled for this account, so a new password would not restore access. Contact Entrilia Support.",
    };
  }
  return {
    canReset: options.passwordReset?.canReset ?? false,
    tooltip: getPasswordResetTooltip(options.passwordReset),
  };
};

export type SignInAction = "contactSupport" | "recoverySteps" | "resetPassword";

export interface SignInRowModel {
  value: string;
  caption: string;
  resetInstruction?: string;
  action?: SignInAction;
  isClearedByReset?: boolean;
}

export const recoveryStepsParagraphs = [
  "Ask the contact to select “Forgot password” on the Investor Portal sign-in page. Microsoft documents this as the " +
    "way to get back in sooner.",
  "A first block normally clears on its own after about a minute. Repeated blocks can last longer, up to five hours.",
  "If they still cannot sign in after that, Entrilia Support can look into it.",
];

const getPasswordChangeRequiredRow = (passwordReset?: ContactPasswordReset): SignInRowModel | undefined => {
  if (passwordReset?.disabledReason === "NotRegistered") {
    return undefined;
  }

  const row: SignInRowModel = {
    value: "Password needs changing",
    caption:
      "The contact must change their password before they can sign in. Ask them to select “Forgot password” on the " +
      "Investor Portal sign-in page, or reset their password here when available.",
    isClearedByReset: true,
  };

  if (passwordReset?.canReset) {
    return { ...row, action: "resetPassword" };
  }
  if (passwordReset?.disabledReason === "PortalAccessInAnotherOrganization") {
    return { ...row, resetInstruction: anotherOrganizationResetInstruction };
  }
  if (passwordReset?.disabledReason === "MissingResetPermissionInOtherClients") {
    return { ...row, resetInstruction: otherClientResetInstruction };
  }
  if (passwordReset?.disabledReason === "RegistrationByEmailInProgress") {
    return { ...row, resetInstruction: emailRegistrationResetInstruction };
  }
  return row;
};

export const getSignInRow = (options: {
  signInState?: ContactSignInState;
  passwordReset?: ContactPasswordReset;
  occurredAtText: string;
}): SignInRowModel | undefined => {
  const { signInState, passwordReset, occurredAtText } = options;

  if (!signInState) {
    return undefined;
  }

  if (signInState.accountStatus === "Disabled") {
    return {
      value: "Sign-in disabled",
      caption:
        "Microsoft Entra reports this account as disabled. It cannot be changed from Admin Portal. Contact Entrilia " +
        "Support.",
      action: "contactSupport",
    };
  }

  switch (signInState.recentIssue?.type) {
    case "RepeatedAttempts":
      return {
        value: "Sign-in temporarily blocked",
        caption:
          `Microsoft temporarily blocked a sign-in attempt at ${occurredAtText} after several unsuccessful attempts. ` +
          "The first blocks normally clear after about one minute, but repeated blocks can last longer. To unlock " +
          "sooner, ask the contact to select “Forgot password” on the Investor Portal sign-in page.",
        action: "recoverySteps",
      };
    case "UnsafeAttempt":
      return {
        value: "Unsafe sign-in attempt blocked",
        caption:
          `Microsoft blocked a sign-in attempt at ${occurredAtText} because it appeared unsafe. If the contact still ` +
          "cannot sign in, contact Entrilia Support.",
        action: "contactSupport",
      };
    case "TemporaryBlock":
      return {
        value: "Sign-in attempt blocked",
        caption:
          `Microsoft blocked a sign-in attempt at ${occurredAtText}. Ask the contact to use “Forgot password” on the ` +
          "Investor Portal sign-in page. Contact Entrilia Support if they still cannot sign in.",
        action: "recoverySteps",
      };
    case "PasswordChangeRequired":
      return getPasswordChangeRequiredRow(passwordReset);
    default:
      return undefined;
  }
};

// B2C's sign-in log lags ~15 minutes, so a just-reset password would keep reporting a problem we already fixed.
export const isSignInRowVisible = (
  row: SignInRowModel | undefined,
  isPasswordJustReset: boolean
): row is SignInRowModel => row !== undefined && !(isPasswordJustReset && row.isClearedByReset === true);

export interface RelatedGuide {
  id: string;
  label: string;
}

export const getRelatedGuides = (options: {
  isRegistered: boolean;
  isRegistrationByEmailRowShown: boolean;
  isImpersonationPossible: boolean;
  isMfaRowShown: boolean;
}): RelatedGuide[] => {
  // Managing access is the one guide that never stops applying — it covers granting access and changing roles alike.
  const guides: RelatedGuide[] = [{ id: intercomArticleIds.MANAGE_PORTAL_USER_ACCESS, label: "Managing access" }];

  if (!options.isRegistered) {
    guides.push({ id: intercomArticleIds.INVESTOR_PORTAL_FOR_LPS, label: "How investors register" });
  }

  if (options.isRegistrationByEmailRowShown) {
    guides.push({ id: intercomArticleIds.REGISTRATION_BY_EMAIL, label: "Registration by email" });
  }

  if (options.isImpersonationPossible) {
    guides.push({ id: intercomArticleIds.IMPERSONATE_CONTACT, label: "Impersonating a contact" });
  }

  if (options.isMfaRowShown) {
    guides.push({ id: intercomArticleIds.MFA, label: "MFA" });
  }

  return guides;
};
